// Wrapper components to provide Chart.js plot viewers
import {
  ChartData,
  ChartDataset,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LegendItem,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { drawPoint } from "chart.js/helpers";

import { FC } from "react";
import { Scatter } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  LinearScale,
  LogarithmicScale,
  Tooltip,
  Legend,
  Title
);

// Set default options
ChartJS.defaults.font = {
  ...ChartJS.defaults.font,
  family: "system-ui",
  size: 16,
};

// Utility to zip lists like in Python
// Thanks to https://stackoverflow.com/a/10284006
export function zip<T>(...args: T[][]): T[][] {
  var shortest =
    args.length == 0
      ? []
      : args.reduce<T[]>(function (a: T[], b: T[]) {
          return a.length < b.length ? a : b;
        }, args[0]);

  return shortest.map(function (_, i) {
    return args.map(function (array) {
      return array[i];
    });
  });
}

// Transform data object from format used in our database to format that can be
// dropped into any of our chart components.
function zipXYData(series_data: ISingleDataSeries) {
  // For now, basically just need to get the data for the x and y axes zipped
  const zipped_data = zip(series_data.xdata, series_data.ydata).map(
    (value, _) => {
      return { x: value[0], y: value[1] };
    }
  );

  return zipped_data;
}

// Same zip function as above, but adapted for the normalized scatter plot API.
function zipXYDataNormalized(series_data: IScatterPlotDataSeries) {
  // For now, basically just need to get the data for the x and y axes zipped
  const zipped_data = zip(series_data.xdata, series_data.ydata).map(
    (value, _) => {
      return { x: value[0], y: value[1] };
    }
  );

  return zipped_data;
}

// Adaptor to transform our data to Chart.JS scatter plot input
// DEPRECATED: not being used anymore in the new normalized plot model.
export function plotJsonToChartJSInput(inputData: ISinglePlotJsonData): {
  options: ChartOptions<"scatter">;
  data: ChartData<"scatter">;
} {
  const options: ChartOptions<"scatter"> = {
    plugins: {
      title: {
        display: true,
        position: "top",
        align: "center",
        text: inputData.meta.name,
        padding: {
          top: 10,
          bottom: 30,
        },
      },
      legend: {
        position: "top",
        title: {
          display: false,
          text: "My legend",
        },
      },
    },
    animation: false,

    scales: {
      x: {
        title: { display: true, text: inputData.x_axis.label },
        type: inputData.x_axis.scale === "log" ? "logarithmic" : "linear",
      },
      yAxisLeft: {
        display: "auto",
        beginAtZero: false,
        title: { display: true, text: inputData.y_axis.label },
        type: inputData.y_axis.scale === "log" ? "logarithmic" : "linear",
        position: "left",
      },
      yAxisRight: {
        display: "auto",
        beginAtZero: false,
        title: {
          display: true,
          text: inputData?.y_axis_2?.label,
          color: "#396b92",
        },
        type: inputData.y_axis_2?.scale === "log" ? "logarithmic" : "linear",
        ticks: { color: "#396b92" },
        grid: { color: "#a1bacb" },
        position: "right",
      },
    },
  };

  const data: ChartData<"scatter"> = {
    datasets: inputData.dataseries.map((single_series: ISingleDataSeries) => ({
      yAxisID: single_series.secondYAxis ? "yAxisRight" : "yAxisLeft",
      label: single_series.name,
      pointStyle: single_series.shape,
      pointRadius: single_series.pointRadius,
      data: zipXYData(single_series),
      backgroundColor: single_series.color,
      borderColor: single_series.color,
      showLine: single_series.showLine,
    })),
  };

  return { options, data };
}

// DEPRECATED: not being used anymore in the normalized plot viewer
export const PlotViewerChartJS: FC<{
  inputData: ISinglePlotJsonData;
  showTitle?: boolean;
}> = ({ inputData, showTitle = true }) => {
  const { options, data } = plotJsonToChartJSInput(inputData);

  if (typeof options?.plugins?.title?.display !== "undefined") {
    options.plugins.title.display = showTitle;
  }

  return (
    <div className="w-full">
      <Scatter options={options} data={data} redraw={false} updateMode="none" />
    </div>
  );
};

// Adaptor to transform our data to Chart.JS scatter plot input
export function plotJsonToChartJSInputNormalized(
  inputData: IScatterPlotWithDataSeries
): {
  options: ChartOptions<"scatter">;
  data: ChartData<"scatter">;
} {
  const options: ChartOptions<"scatter"> = {
    plugins: {
      title: {
        display: true,
        position: "top",
        align: "center",
        text: inputData.name,
        padding: {
          top: 10,
          bottom: 30,
        },
      },
      legend: {
        position: "top",
        title: {
          display: false,
          text: "My legend",
        },
        labels: {
          usePointStyle: true,
          useBorderRadius: true,
          // Hacky way to try to show both a line and the point symbol on each
          // legend entry. Not the best because now each legend entry has a
          // line, even if the line is hidden in the actual plot.

          // Thanks to https://stackoverflow.com/a/75851008.
          generateLabels(chart) {
            const datasets = chart.data.datasets;
            const all_metas = [...Array(chart.data.datasets.length).keys()].map(
              (idx) => chart.getDatasetMeta(idx)
            );
            return all_metas.map((meta) => {
              const style = meta.controller.getStyle(meta.index, true);
              const dataset = datasets[meta.index] as ChartDataset<"scatter">;
              return {
                text: dataset.label ? dataset.label : "Generic label",
                hidden: !meta.visible,
                pointStyle: (() => {
                  const canvas = document.createElement("canvas");
                  canvas.setAttribute("width", "50");
                  canvas.setAttribute("height", "20");
                  const context = canvas.getContext("2d");
                  if (!context) return canvas;

                  // TODO: Want some way to detect if the original dataset had
                  // showLine set to false and if so to skip drawing the line.
                  context.lineWidth = style.borderWidth;
                  context.lineCap = style.borderCapStyle;
                  context.lineJoin = style.borderJoinStyle;
                  context.setLineDash(
                    style.borderDash ? style.borderDash : [0.5, 0]
                  );
                  context.lineDashOffset = style.lineDashOffset;
                  context.strokeStyle = style.borderColor;
                  context.fillStyle = style.backgroundColor;
                  if (dataset.showLine ? dataset.showLine : false) {
                    // draw the line
                    context.beginPath();
                    context.moveTo(10, 10);
                    context.lineTo(40, 10);
                    context.stroke();
                    context.setLineDash([]);
                    context.lineWidth = 1;
                  }

                  // Now draw whatever symbol has been used for this dataset in
                  // the middle of the line.
                  // context.beginPath();
                  // context.arc(25, 10, 4, 0, 2 * Math.PI);
                  // context.fill();
                  drawPoint(
                    context,
                    {
                      pointStyle: style.pointStyle,
                      radius: style.radius * 2,
                      borderWidth: dataset.borderWidth,
                    },
                    25,
                    10
                  );
                  return canvas;
                })() as unknown as LegendItem,
                datasetIndex: meta.index,
              } as unknown as LegendItem;
            });
          },
        },
      },
    },
    animation: false,

    scales: {
      x: {
        title: { display: true, text: inputData.x_axis_name },
        type: inputData.x_axis_scale === "log" ? "logarithmic" : "linear",
        reverse: inputData.x_axis_reversed,
      },
      yAxisLeft: {
        display: "auto",
        beginAtZero: false,
        title: { display: true, text: inputData.y_axis_name },
        type: inputData.y_axis_scale === "log" ? "logarithmic" : "linear",
        position: "left",
        reverse: inputData.y_axis_reversed,
      },
      yAxisRight: {
        display: "auto",
        beginAtZero: false,
        title: {
          display: true,
          text: inputData?.y_axis_2_name,
          color: "#396b92",
        },
        type: inputData.y_axis_2_scale === "log" ? "logarithmic" : "linear",
        ticks: { color: "#396b92" },
        grid: { color: "#a1bacb" },
        position: "right",
        reverse: inputData.y_axis_2_reversed,
      },
    },
  };

  // Want to render data series in the same order as specified in the scatter
  // plot record. Check if the "data_series_order" field is set and matches the
  // IDs of the plots we're using.
  let dataSeriesToUse = getOrderedDataSeries(inputData);

  const data: ChartData<"scatter"> = {
    datasets: dataSeriesToUse.map((single_series: IScatterPlotDataSeries) => ({
      yAxisID: single_series.secondYAxis ? "yAxisRight" : "yAxisLeft",
      label: single_series.name,
      pointStyle: single_series.shape === "false" ? false : single_series.shape,
      pointRadius: single_series.pointRadius,
      data: zipXYDataNormalized(single_series),
      backgroundColor: single_series.fillPoints
        ? single_series.color
        : ChartJS.defaults.backgroundColor,
      borderColor: single_series.color,
      borderWidth: single_series.lineThickness,
      showLine: single_series.showLine,
    })),
  };

  return { options, data };
}

export const PlotViewerChartJSNormalized: FC<{
  inputData: IScatterPlotWithDataSeries;
  showTitle?: boolean;
}> = ({ inputData, showTitle = true }) => {
  const { options, data } = plotJsonToChartJSInputNormalized(inputData);

  if (typeof options?.plugins?.title?.display !== "undefined") {
    options.plugins.title.display = showTitle;
  }

  return (
    <div className="w-full">
      <Scatter options={options} data={data} redraw={false} updateMode="none" />
    </div>
  );
};

export function getOrderedDataSeries(inputData: IScatterPlotWithDataSeries) {
  const child_data_series_ids = new Set(
    inputData.data_series.map(
      (single_series: IScatterPlotDataSeries) => single_series.id
    )
  );

  const ordering_data_series_ids = new Set(inputData.data_series_order);

  const shouldUseStoredOrder =
    child_data_series_ids.size === ordering_data_series_ids.size &&
    [...child_data_series_ids].every((x: number) =>
      ordering_data_series_ids.has(x)
    );

  let dataSeriesToUse;
  if (shouldUseStoredOrder) {
    dataSeriesToUse = Array.from(ordering_data_series_ids).map(
      (id: number) =>
        inputData.data_series.filter(
          (series: IScatterPlotDataSeries) => series.id === id
        )[0]
    );
  } else {
    dataSeriesToUse = inputData.data_series;
  }
  return dataSeriesToUse;
}
